import React, { useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { ErrorText, FieldGroup, ModalFooter, PillButton, Stack, Text, TextInput } from 'library/ui';
import { createSetupIntent, savePaymentMethod } from './api';
import { readPaymentMethodId } from './stripeHelpers';
import { PaymentMethod, PaymentProduct } from './types';

interface AddBankAccountFormProps {
    onSuccess: (method: PaymentMethod) => void;
    onCancel?: () => void;
    cancelLabel?: string;
    product?: PaymentProduct;
}

interface BankConfirmDetails {
    last4?: string;
    routing?: string;
}

export function AddBankAccountForm({
    onSuccess,
    onCancel,
    cancelLabel = 'Cancel',
    product = 'subs',
}: AddBankAccountFormProps) {
    const stripe = useStripe();
    const [accountHolderName, setAccountHolderName] = useState('');
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [confirmDetails, setConfirmDetails] = useState<BankConfirmDetails | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inConfirmation = confirmDetails !== null;

    async function startBankCollection() {
        if (!stripe) return;
        setSubmitting(true);
        setError(null);
        try {
            const { client_secret } = await createSetupIntent('us_bank_account');
            setClientSecret(client_secret);

            const result = await stripe.collectBankAccountForSetup({
                clientSecret: client_secret,
                params: {
                    payment_method_type: 'us_bank_account',
                    payment_method_data: {
                        billing_details: { name: accountHolderName || undefined },
                    },
                },
                expand: ['payment_method'],
            });

            if (result.error) {
                setError(result.error.message ?? 'Could not connect bank account.');
                setClientSecret(null);
                setSubmitting(false);
                return;
            }

            const setupIntent = result.setupIntent;
            if (setupIntent.status === 'requires_payment_method') {
                setClientSecret(null);
                setSubmitting(false);
                return;
            }

            if (setupIntent.status === 'requires_confirmation' && typeof setupIntent.payment_method !== 'string') {
                const us = setupIntent.payment_method?.us_bank_account;
                setConfirmDetails({ last4: us?.last4, routing: us?.routing_number ?? undefined });
                setSubmitting(false);
                return;
            }

            setSubmitting(false);
        } catch {
            setError('Could not connect bank account. Please try again.');
            setClientSecret(null);
            setSubmitting(false);
        }
    }

    async function confirmBankAccount() {
        if (!stripe || !clientSecret) return;
        setSubmitting(true);
        setError(null);
        try {
            const result = await stripe.confirmUsBankAccountSetup(clientSecret);
            if (result.error) {
                setError(result.error.message ?? 'Could not confirm bank account.');
                setSubmitting(false);
                return;
            }
            const setupIntent = result.setupIntent;
            const paymentMethodId = readPaymentMethodId(result);
            if (!paymentMethodId) {
                setError('Stripe did not return a payment method id.');
                setSubmitting(false);
                return;
            }
            const saved = await savePaymentMethod(paymentMethodId, setupIntent.status, product);
            setSubmitting(false);
            onSuccess(saved);
        } catch {
            setError('Could not save bank account. Please try again.');
            setSubmitting(false);
        }
    }

    return (
        <Stack gap="md">
            {!inConfirmation && (
                <FieldGroup label="Account Holder Name">
                    <TextInput
                        value={accountHolderName}
                        onChange={setAccountHolderName}
                        placeholder="Name on account"
                        fullWidth
                    />
                </FieldGroup>
            )}
            {inConfirmation && confirmDetails && (
                <Stack gap="sm">
                    <Text>
                        <strong>Account ending in:</strong> {confirmDetails.last4 ?? '????'}
                    </Text>
                    {confirmDetails.routing && (
                        <Text>
                            <strong>Routing number:</strong> {confirmDetails.routing}
                        </Text>
                    )}
                    <Text tone="secondary" size="sm">
                        Confirm to save this bank account.
                    </Text>
                </Stack>
            )}
            {error && <ErrorText>{error}</ErrorText>}
            {!stripe && <Text tone="secondary">Loading Stripe…</Text>}
            <ModalFooter>
                {onCancel && (
                    <PillButton variant="secondary" onClick={onCancel} disabled={submitting}>
                        {cancelLabel}
                    </PillButton>
                )}
                <PillButton
                    variant="primary"
                    onClick={inConfirmation ? confirmBankAccount : startBankCollection}
                    disabled={!stripe || submitting || !accountHolderName}
                >
                    {submitting ? 'Saving…' : inConfirmation ? 'Confirm Bank Account' : 'Connect Bank Account'}
                </PillButton>
            </ModalFooter>
        </Stack>
    );
}
